<template>
	<BaseModal
		:modalTitle="contactFullName"
		:showHeader="currentView !== VIEWS.CONFIRM_FILTER_SELECTION"
		:showFooter="false"
		@close="handleClose"
	>
		<div
			slot="modalHeader"
			v-if="currentView !== VIEWS.CONFIRM_FILTER_SELECTION"
		>
			<OverviewModalHeader
				:currentView="currentHeader"
				:currentAlertPreference="currentPreference"
				:contactName="contactFullName"
				:removeBackCaret="removeBackCaret"
				@close="handleClose"
				@back="handlePrevious"
			/>
		</div>
		<div slot="modalBody">
			<LoadingIcon v-if="isLoading" alignment="center" />
			<div v-if="!isLoading">
				<div v-if="currentView === VIEWS.HOME && !isEmpty(leadDetails)">
					<OverviewModalHome
						:leadDetails="leadDetails"
						:blinkDisabled="blinkDisabled"
						:loggedInUserId="loggedInUserId"
						@onTabClick="handleTabClick"
						@editContactClick="handleEditContact"
						@transferContactClick="handleTransferContact"
						@deactivateContactClick="handleDeactivateContact"
						@sendToBlinkClick="handleSendToBlink"
						@addLeadNote="handleAddLeadNoteClick"
						@sendToVendor="handleSendToVendor"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.ALERT_LIST">
					<AlertsList
						:contactId="leadDetails.contactId"
						:leadId="leadId"
						:blinkDisabled="blinkDisabled"
						:loggedInUserId="loggedInUserId"
						@onAlertClick="handleAlertClick"
						@onAlertNoteClick="handleAddAlertNoteClick"
						@statusUpdated="$emit('alertStatusUpdated')"
						@sendToBlinkClick="handleSendToBlink"
					/>
				</div>
				<div
					v-else-if="
						currentView === VIEWS.CONTACT_DETAILS ||
						currentView === VIEWS.ADD_CONTACT
					"
				>
					<ContactDetails
						:contactDetails="leadDetails"
						@onContactSave="handleContactSave"
						@onCancel="handleClose"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.ADD_NOTE">
					<AddNote
						:leadId="leadId"
						:alertId="currentAlertId"
						:type="this.noteType"
						@leadNoteSaved="handleClose"
						@alertNoteSaved="currentView = VIEWS.ALERT_DETAILS"
						@close="handleClose"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.ALERT_DETAILS">
					<AlertDetails
						:contactId="leadDetails.contactId"
						:alertId="currentAlertId"
						:blinkDisabled="blinkDisabled"
						:loggedInUserId="loggedInUserId"
						@onLoanClick="handleLoanClick"
						@statusUpdated="$emit('alertStatusUpdated')"
						@addNote="handleAddAlertNoteClick(currentAlertId)"
						@selectName="handleNameClick"
						@sendToBlinkClick="handleSendToBlink"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.LOANS_LIST">
					<LoansList
						:contactId="leadDetails.contactId"
						:leadId="leadId ? leadId : leadDetails.leadId"
						:blinkDisabled="blinkDisabled"
						:loggedInUserId="loggedInUserId"
						@onLoanClick="handleLoanClick"
						@onDeleteLoanClick="handleDeleteLoanClick"
						@sendToBlinkClick="handleSendLoanToBlink"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.LOAN_DETAILS">
					<LoanDetails
						:loanId="currentLoanId"
						:leadId="leadId ? leadId : leadDetails.leadId"
						:contactId="leadDetails.contactId"
						:entityId="leadDetails.entityId"
						:blinkDisabled="blinkDisabled"
						:loggedInUserId="loggedInUserId"
						@onDeleteLoanClick="handleDeleteLoanClick"
						@onDuplicateLoanError="handleChangeView"
						@configureAlerts="handleConfigureAlerts"
						@setUWMLoanInd="(uwmInd) => (isUWMLoan = uwmInd)"
						@onCloseLoanDetails="handleCloseLoanDetails"
						@sendToBlinkClick="handleSendLoanToBlink"
						@newLoanAdded="currentLoanId = $event"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.DELETE_LOAN">
					<DeleteLoan
						:loanId="loanIdToDelete"
						@loanDeleted="handleLoanDeleted"
						@close="handleClose"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.CONTACT_SETTINGS">
					<ContactSettings
						:contactId="leadId"
						@configureAlerts="handleConfigureAlerts"
						@onLoanClick="handleLoanClick"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.ALERT_SETTINGS">
					<AlertSettings
						:preference="currentPreference"
						@close="handleClose"
						:isUWMContact="leadDetails.uwmInd"
						:isUWMLoan="isUWMLoan"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.DUPLICATE_CONTACT">
					<DuplicateContact @close="handleClose" :history="history" />
				</div>
				<div v-else-if="currentView === VIEWS.INACTIVE_CONTACT">
					<ReactivateSingleContact
						@close="handleClose"
						@reactivateSingleContact="handleReactivateSingleContact"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.ACTIVITY">
					<ActivityLog
						:leadId="leadId"
						:contactName="contactFullName"
						@close="handleClose"
						@onAlertClick="handleAlertClick"
						@onAddressClick="handleLoanClick"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.SEND_TO_BLINK">
					<SendToBlink
						:success="success"
						:waiting="waiting"
						:leadId="leadId"
						:leadData="leadInfo"
						:sendContactToBlink="sendContactToBlink"
						:sendLoanInfoToBlink="sendLoanToBlink"
						:blinkUnregistered="blinkUnregistered"
						@handleClose="handleClose"
						@leadSentToBlink="handleClose"
						@loanSentToBlink="handleClose"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.SEND_TO_VENDOR">
					<SendToVendor
						:success="success"
						:waiting="waiting"
						:leadId="leadId"
						:leadData="leadInfo"
						:vendorLinkRegistered="userHasBigMacLink"
						:leadIdList="selectedLeadIds"
						@handleClose="handleClose"
						@vendorLinkSent="handleClose"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.CONFIRM_FILTER_SELECTION">
					<ConfirmFilterSelection
						@confirmFilterSelection="handleConfirmFilterSelection"
						@cancelFilterSelection="handleCancelFilterSelection"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.DUPLICATE_LOAN" slot="modalBody">
					<DuplicateLoan @ok="handleCloseLoanDetails" />
				</div>
				<div v-else-if="currentView === VIEWS.DEACTIVATE_CONTACT">
					<DeactivateContact
						:companyIds="companyIds"
						:leadId="leadId || this.leadDetails.leadId"
						:selectAllMode="selectAllMode"
						:selectedLeadIds="selectedLeadIds"
						@clearLeadIds="$emit('clearLeadIds')"
						@filterLeadId="$emit('filterLeadId')"
						@handleClose="handleClose"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.REACTIVATE_CONTACT">
					<ReactivateContact
						:companyIds="companyIds"
						:leadId="leadId || this.leadDetails.leadId"
						:selectAllMode="selectAllMode"
						:selectedLeadIds="selectedLeadIds"
						@clearLeadIds="$emit('clearLeadIds')"
						@filterLeadId="$emit('filterLeadId')"
						@handleClose="handleClose"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.TRANSFER_CONTACT">
					<TransferContact
						:brokerContactsList="brokerContacts"
						:companyIds="companyIds"
						:leadId="leadId"
						:selectAllMode="selectAllMode"
						:selectedLeadIds="selectedLeadIds"
						@handleClose="handleClose"
						@clearLeadIds="$emit('clearLeadIds')"
						@filterLeadId="$emit('filterLeadId')"
					/>
				</div>
				<div v-else-if="currentView === VIEWS.UPDATE_CONTACT_TYPE">
					<UpdateContactType
						:companyIds="companyIds"
						:leadId="leadId"
						:selectAllMode="selectAllMode"
						:selectedLeadIds="selectedLeadIds"
						@clearLeadIds="$emit('clearLeadIds')"
						@filterLeadId="$emit('filterLeadId')"
						@handleClose="handleClose"
					/>
				</div>
				<div v-else>
					<div class="coming-soon">Coming soon</div>
				</div>
			</div>
		</div>
	</BaseModal>
</template>

<script>
import { isEmpty } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';

import ActivityLog from './ActivityLog.vue';
import AlertDetails from './AlertDetails.vue';
import AlertsList from './AlertsList.vue';
import AlertSettings from './AlertSettings.vue';
import AddNote from './AddNote.vue';
import BaseModal from '@/components/global/BaseModal.vue';
import ConfirmFilterSelection from './ConfirmFilterSelection.vue';
import ContactDetails from './ContactDetails.vue';
import ContactSettings from './ContactSettings.vue';
import DeleteLoan from './DeleteLoan.vue';
import DuplicateContact from './DuplicateContact.vue';
import DuplicateLoan from './DuplicateLoan.vue';
import ReactivateSingleContact from './ReactivateSingleContact.vue';
import LoadingIcon from '../../../components/global/LoadingIcon.vue';
import LoanDetails from './LoanDetails.vue';
import LoansList from './LoansList.vue';
import OverviewModalHeader from './OverviewModalHeader.vue';
import OverviewModalHome from './OverviewModalHome.vue';
import TransferContact from './TransferContact.vue';
import DeactivateContact from './DeactivateContact.vue';
import ReactivateContact from './ReactivateContact.vue';
import SendToBlink from './SendToBlink.vue';
import SendToVendor from './SendToVendor.vue';
import UpdateContactType from './UpdateContactType.vue';
import { VIEWS } from './constants';

export default {
	name: 'OverviewModal',
	components: {
		ActivityLog,
		AlertDetails,
		AlertsList,
		AlertSettings,
		AddNote,
		BaseModal,
		ConfirmFilterSelection,
		ContactDetails,
		ContactSettings,
		DeleteLoan,
		DuplicateContact,
		DuplicateLoan,
		ReactivateSingleContact,
		LoadingIcon,
		LoansList,
		LoanDetails,
		OverviewModalHeader,
		OverviewModalHome,
		TransferContact,
		DeactivateContact,
		ReactivateContact,
		SendToBlink,
		SendToVendor,
		UpdateContactType,
	},
	props: {
		alertId: Number,
		blinkDisabled: Boolean,
		blinkUnregistered: Boolean,
		entityId: Number,
		leadId: Number,
		leadInfo: Object,
		loggedInUserId: Number,
		modalHistory: {
			type: Array,
			default: () => [],
		},
		modalPreference: Object,
		modalView: String,
		removeBackCaret: Boolean,
		selectAllMode: Boolean,
		selectedLeadIds: Array,
		selectedLeadsCount: Number,
		sendContact: Boolean,
		typeOfNote: String,
	},
	computed: {
		...mapState({
			contactId: (state) => state.profile.rolodexContactId,
			alertsView: (state) => state.clientConnectV2.alertsView,
			userHasBigMacLink: (state) => !!state.profile.bigMacLink,
			searchTerm: (state) => state.ccContacts.filters.searchTerm,
			selectedContactsIds: (state) => state.clientConnectV2.selectedContactsIds,
			totalCount: (state) => state.ccContacts.leads.totalCount,
		}),
		...mapGetters([
			'brokerContactsList',
			'currentImpersonatedUserRolodexId',
			'currentUserRolodexIds',
			'isImpersonated',
		]),
		contactFullName() {
			return this.leadDetails
				? `${this.leadDetails.firstName} ${this.leadDetails.lastName}`
				: '';
		},
		companyIds() {
			const { entityId, contactsIds } = !this.isImpersonated
				? this.currentUserRolodexIds
				: this.currentImpersonatedUserRolodexId;
			return [entityId, contactsIds];
		},
		currentHeader() {
			if (
				this.currentView === VIEWS.REACTIVATE_CONTACT &&
				this.multipleLeadsSelected
			) {
				return VIEWS.REACTIVATE_CONTACTS;
			} else if (
				this.currentView === VIEWS.DEACTIVATE_CONTACT &&
				this.multipleLeadsSelected
			) {
				return VIEWS.DEACTIVATE_CONTACTS;
			} else {
				return this.currentView;
			}
		},
		multipleLeadsSelected() {
			if (this.selectAllMode) {
				return this.selectedLeadsCount < this.totalCount - 1 && !this.leadId;
			}
			return this.selectedLeadsCount > 1 && !this.leadId;
		},
	},
	data() {
		return {
			leadDetails: {},
			alertDetails: null,
			currentView: VIEWS.HOME,
			currentAlertId: null,
			currentLeadId: null,
			currentLoanId: null,
			currentAlertSetting: null,
			currentPreference: null,
			loanIdToDelete: null,
			history: [],
			VIEWS,
			noteType: this.typeOfNote,
			isUWMLoan: null,
			isLoading: false,
			brokerContacts: [],
			success: false,
			waiting: false,
			leadData: this.leadInfo,
			sendLoanToBlink: false,
			sendContactToBlink: this.sendContact,
		};
	},
	methods: {
		...mapActions([
			'getLeadDetailsById',
			'getAlertDetailsById',
			'clearClientConnectAlerts',
			'saveLeadDetails',
			'setLoanDetailsView',
			'getClientConnectLeads',
			'getClientConnectAlerts',
		]),
		/**
		 * when we use a function in the template that is imported from outside
		 * the component it needs to be included in the methods object
		 */
		// deepcode ignore VueIncorrectMethodType: see note above
		isEmpty,
		handleClose() {
			this.$emit('close');
		},
		handleTabClick(view) {
			this.handleChangeView(view);
		},
		handleAlertClick(alertId) {
			this.currentAlertId = alertId;
			this.handleChangeView(VIEWS.ALERT_DETAILS);
		},
		handleLoanClick(loanId, view) {
			this.currentLoanId = loanId;
			this.setLoanDetailsView(view);
			this.handleChangeView(VIEWS.LOAN_DETAILS);
		},
		handleCloseLoanDetails() {
			this.history = [VIEWS.HOME];
			this.handleChangeView(VIEWS.LOANS_LIST);
		},
		handleDeleteLoanClick(loanId) {
			this.loanIdToDelete = loanId;
			this.handleChangeView(VIEWS.DELETE_LOAN);
		},
		handleEditContact() {
			this.handleChangeView(VIEWS.CONTACT_DETAILS);
		},
		handleTransferContact() {
			this.handleChangeView(VIEWS.TRANSFER_CONTACT);
		},
		handleSendToBlink() {
			this.sendContactToBlink = true;
			this.sendLoanToBlink = false;
			this.handleChangeView(VIEWS.SEND_TO_BLINK);
		},
		handleSendToVendor() {
			this.handleChangeView(VIEWS.SEND_TO_VENDOR);
		},
		handleSendLoanToBlink(loanId, propertyAddress) {
			this.leadInfo.loanId = loanId;
			this.leadInfo.address = propertyAddress;
			this.sendContactToBlink = false;
			this.sendLoanToBlink = true;
			this.handleChangeView(VIEWS.SEND_TO_BLINK);
		},
		handleDeactivateContact() {
			this.handleChangeView(VIEWS.DEACTIVATE_CONTACT);
		},
		handleNameClick() {
			this.handleChangeView(VIEWS.HOME);
		},
		handleAddAlertNoteClick(alertId) {
			this.currentAlertId = alertId;
			this.noteType = 'alert';
			this.handleChangeView(VIEWS.ADD_NOTE);
		},
		handleAddLeadNoteClick(leadId) {
			this.currentLeadId = leadId;
			this.noteType = 'lead';
			this.handleChangeView(VIEWS.ADD_NOTE);
		},
		handleConfigureAlerts(currentPreference) {
			this.currentPreference = currentPreference;
			this.handleChangeView(currentPreference.view);
		},
		handleLoanDeleted() {
			this.history = [VIEWS.HOME];
			this.handleChangeView(VIEWS.LOANS_LIST);
		},
		handlePrevious() {
			this.history.shift();
			this.currentView = isEmpty(this.history) ? VIEWS.HOME : this.history[0];
			this.getLeadDetails();
		},
		handleChangeView(view) {
			this.currentView = view;
			this.history.unshift(view);
		},
		async getLeadDetails() {
			if (this.leadId) {
				this.isLoading = true;
				this.leadDetails = await this.getLeadDetailsById(this.leadId);
				this.isLoading = false;
			}
		},
		handleConfirmFilterSelection() {
			this.$emit('confirmFilterSelection');
		},
		handleCancelFilterSelection() {
			this.$emit('cancelFilterSelection');
		},
		// Saving contacts
		handleInactiveContact(view) {
			this.handleChangeView(view);
		},
		async handleReactivateSingleContact() {
			await this.saveContact();
			this.$emit('updateContactList', { id: this.leadDetails.leadId });
		},
		async handleContactSave({ contact, newContact }) {
			this.leadDetails = contact;
			await this.saveContact();
			if (newContact) {
				this.$emit('updateContactList', { id: contact.leadId });
			}
			this.$emit('refreshCurrentPageContacts');
		},
		async saveContact() {
			// create payload
			let payload = {
				contactId: this.contactId,
				entityId: this.currentUserRolodexIds.entityId,
				...this.leadDetails,
				...{ leadTypeId: this.leadDetails.type.id },
			};

			payload.ficoScore =
				payload.ficoScore === '' ? null : parseInt(payload.ficoScore, 10);
			// save lead

			try {
				this.isLoading = true;
				const { lead, leadstatus } = await this.saveLeadDetails(payload);
				// handle exceptions
				if (
					!leadstatus.isSuccess &&
					leadstatus.errorMessage.includes('Duplicate Lead')
				) {
					this.isLoading = false;
					this.handleChangeView(VIEWS.DUPLICATE_CONTACT);
				} else if (
					!leadstatus.isSuccess &&
					leadstatus.errorMessage.includes('Inactive Lead Exists')
				) {
					this.isLoading = false;
					const leadId = leadstatus.errorMessage.match(/\d+/)[0];
					this.leadDetails.leadId = parseInt(leadId, 10);
					this.handleInactiveContact(VIEWS.INACTIVE_CONTACT);
				} else {
					// finish up
					this.leadDetails = lead;
					if (isEmpty(lead.phones)) this.formatPhones();
					this.formatBirthday();
					await this.refreshAlerts(true);
					this.isLoading = false;
					this.handleChangeView(VIEWS.HOME);
				}
			} catch (error) {
				this.isLoading = false;
				this.$emit('apiCallError', 'saveContact');
			}
		},
		async refreshAlerts(bySearchTerm) {
			try {
				const { entityId, contactsIds } = this.currentUserRolodexIds;
				const payload = {
					data: {
						entityId,
						contactsIds: isEmpty(this.selectedContactsIds)
							? contactsIds
							: this.selectedContactsIds,
						typesIds: this.alertsView.typesIds,
						statusesIds: this.alertsView.statusesIds,
					},
					searchTerm: this.alertsView.searchTerm,
					sortColumn: this.alertsView.sortColumn,
					sortDirection: this.alertsView.sortDirection,
					pageNumber: this.alertsView.pageNumber,
					pageSize: this.alertsView.pageSize,
					dateRange: this.alertsView.dateRange,
				};

				await this.getClientConnectAlerts({ payload, bySearchTerm });
			} catch (error) {
				this.error = error;
			}
		},
		formatPhones() {
			this.leadDetails = {
				...this.leadDetails,
				...{
					phones: [
						{
							phoneExt: null,
							phoneNumber: null,
							type: {
								phoneTypeId: null,
							},
						},
					],
				},
			};
		},
		formatBirthday() {
			let date = this.leadDetails.birthday;
			// if no birthday was saved, the api returns 01-01-0001, so set it to null
			if (new Date(date).getFullYear() === 1) {
				date = null;
			} else {
				date = new Date(date);
			}
			this.leadDetails = {
				...this.leadDetails,
				...{ birthday: date },
			};
		},
	},
	async created() {
		await this.getLeadDetails();
		this.brokerContacts = this.brokerContactsList.map((contact) => ({
			name: `${contact.firstName} ${contact.lastName}`,
			contactId: contact.rolodexContactId || contact.contactId,
			value: contact.rolodexContactId || contact.contactId,
		}));
	},
	mounted() {
		this.history = this.modalHistory;
		this.handleChangeView(this.modalView);
		this.currentAlertId = this.alertId;
		this.currentLeadId = this.leadId;
		this.currentPreference = this.modalPreference;
	},
	beforeDestroy() {
		this.handleClose();
	},
	watch: {
		modalView(value) {
			this.handleChangeView(value);
		},
	},
};
</script>

<style lang="scss" scoped>
@media only screen and (max-width: 600px) {
	.name-container {
		display: flex;
		flex-direction: column;
	}
}

.coming-soon {
	display: flex;
	flex-wrap: wrap;
	font-weight: 500;
	justify-content: center;
	align-items: center;
	@include rem('padding', 50px);
	@include rem('font-size', 22px);
}

.tab-container {
	display: flex;
	flex-direction: column;

	.tab {
		display: grid;
		align-items: center;
		grid-template-columns: 44px auto 20px;
		border-top: 1px solid $border-color-light;
		@include rem('gap', 16px);
		@include rem('padding', 18px);

		.tab-name {
			font-weight: 500;
			@include rem('font-size', 17px);
			color: $brand-blue;
			cursor: pointer;
		}

		.tab-icon {
			color: $black;
			background-color: $white;
			border: 1px solid $border-color-light;
			border-radius: 6px;
			@include rem('padding', 12px);
			@include rem('font-size', 22px);
			cursor: pointer;
		}

		.tab-description {
			color: $b360-color--gray__darker;
			cursor: pointer;
		}

		.chevron-right-icon {
			@include rem('font-size', 18px);
			color: $black;
			cursor: pointer;
		}
	}
}
</style>
